import { useCallback, useEffect, useState } from "react";
import { normalizeNetworkQuality } from "@/utils/networkQuality";

const getNavigatorConnection = () => {
  if (typeof navigator === "undefined") {
    return null;
  }
  return (
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null
  );
};

const readNetworkSnapshot = () => {
  if (typeof navigator === "undefined") {
    return normalizeNetworkQuality({ online: true });
  }

  const connection = getNavigatorConnection();
  const downlink =
    connection && typeof connection.downlink === "number"
      ? connection.downlink
      : undefined;
  const rtt =
    connection && typeof connection.rtt === "number" ? connection.rtt : undefined;

  return normalizeNetworkQuality({
    online: navigator.onLine,
    effectiveType: connection?.effectiveType,
    downlink,
    rtt,
  });
};

export const useNetworkQuality = () => {
  const getSnapshot = useCallback(() => readNetworkSnapshot(), []);
  const [state, setState] = useState(() => getSnapshot());

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleChange = () => {
      setState(getSnapshot());
    };

    window.addEventListener("online", handleChange);
    window.addEventListener("offline", handleChange);

    const connection = getNavigatorConnection();
    let unsubscribeConnectionChange;

    if (connection) {
      if (typeof connection.addEventListener === "function") {
        connection.addEventListener("change", handleChange);
        unsubscribeConnectionChange = () =>
          connection.removeEventListener("change", handleChange);
      } else if ("onchange" in connection) {
        const previousHandler = connection.onchange;
        connection.onchange = handleChange;
        unsubscribeConnectionChange = () => {
          if (connection.onchange === handleChange) {
            connection.onchange = previousHandler;
          }
        };
      }
    }

    return () => {
      window.removeEventListener("online", handleChange);
      window.removeEventListener("offline", handleChange);
      unsubscribeConnectionChange?.();
    };
  }, [getSnapshot]);

  return state;
};
